const RASTERIZE_TIMEOUT_MS = 5_000;
const MAX_CACHE_SIZE = 200;

const cache = new Map<string, Promise<string>>();

/**
 * Resolves a game image URL into a static CSS `url(...)` value.
 * The image is always rasterized to a single-frame WebP via an
 * offscreen canvas. This freezes any animation (SVG SMIL, animated
 * WebP, APNG) to the first decoded frame and avoids ongoing
 * animation cost when used as a CSS background.
 */
export async function resolveAndRasterizeGameAssetUrl(src: string): Promise<string> {
  const dataUrl = await rasterizeToFirstFrame(src);
  return `url("${dataUrl}")`;
}

/**
 * Rasterizes an image URL to its first decoded frame as a WebP data URL.
 * Useful for freezing animated SVGs, APNGs, or animated WebPs into a
 * static raster. Returns the original `src` unchanged on any failure
 * (network error, CORS, tainted canvas, timeout).
 *
 * Results are cached per source URL — repeated calls with the same
 * `src` return the same Promise without re-decoding.
 */
export function rasterizeToFirstFrame(src: string): Promise<string> {
  const cached = cache.get(src);
  if (cached) return cached;

  // Evict oldest entry when cache reaches capacity
  if (cache.size >= MAX_CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }

  const promise = _rasterize(src);
  cache.set(src, promise);
  return promise;
}

function _rasterize(src: string): Promise<string> {
  return new Promise((resolve) => {
    let settled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const timer = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        img.src = ''; // cancel any in-progress network request
        resolve(src);
      }
    }, RASTERIZE_TIMEOUT_MS);

    const cleanup = () => window.clearTimeout(timer);

    img.onload = () => {
      if (settled) return;
      settled = true;
      cleanup();

      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          // Cannot determine intrinsic dimensions (e.g. SVG with
          // only a viewBox) — skip rasterization to avoid distortion.
          resolve(src);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(src);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/webp'));
      } catch {
        // Canvas tainted or other security restriction — fall back
        resolve(src);
      }
    };

    img.onerror = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(src);
    };

    img.src = src;
  });
}
